import { parseMarkdown, type Block } from '@st/util/markdown'
import { defModule, Task, type TaskMessage } from '@st/redux'
import type { PaymentInstructions } from '@st/sdk'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import { SdkClient } from '~/effects'
import { observeCurrentUser, observeRoute, runSdkRequest } from '~/features/module-runtime'

const observeGuideRoute = observeRoute({ name: 'payment_guide' })
type GuideRoute = TaskMessage<typeof observeGuideRoute>['route']
const loadGuide = Task.define(
  (userId: string, route: NonNullable<GuideRoute>) =>
    Effect.gen(function* () {
      const sdk = yield* SdkClient
      const result = yield* runSdkRequest(
        sdk.send({ type: 'taxdb/getPaymentInstructions', ...route })
      )
      if (!result.ok) return { type: 'loaded', content: { status: 'failed' } } as const
      const instructions = result.response.instructions
      let guide: (PaymentInstructions & { blocks: Block[] }) | undefined
      if (instructions)
        guide = { ...instructions, blocks: parseMarkdown(instructions.instructions) }
      return { type: 'loaded', content: { status: 'ready', guide } } as const
    }),
  {
    key: (userId, route) => JSON.stringify([userId, route])
  }
)
type Content =
  | { status: 'signedOut' }
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; guide: (PaymentInstructions & { blocks: Block[] }) | undefined }
type State = { route: GuideRoute; userId: string | null | undefined; content: Content }
type Message =
  | TaskMessage<typeof observeGuideRoute>
  | TaskMessage<typeof observeCurrentUser>
  | TaskMessage<typeof loadGuide>
  | { type: 'retry' }
export const paymentGuideModule = defModule<{ state: State; message: Message }>({
  init: () => ({ route: null, userId: undefined, content: { status: 'loading' } }),
  tasks: (state) => {
    const observers = [observeGuideRoute(), observeCurrentUser()]
    if (state.route && state.userId && state.content.status == 'loading')
      return [...observers, loadGuide(state.userId, state.route)]
    return observers
  },
  update: (state, message): State =>
    match(message)
      .with({ type: 'routeChanged' }, ({ route }) => ({
        ...state,
        route,
        content: match(state.userId)
          .with(null, () => ({ status: 'signedOut' as const }))
          .otherwise(() => ({ status: 'loading' as const }))
      }))
      .with({ type: 'currentUserChanged' }, ({ currentUserId }) => ({
        ...state,
        userId: currentUserId,
        content: match(currentUserId)
          .with(null, () => ({ status: 'signedOut' as const }))
          .otherwise(() => ({ status: 'loading' as const }))
      }))
      .with({ type: 'loaded' }, ({ content }) => ({ ...state, content }))
      .with({ type: 'retry' }, () => ({ ...state, content: { status: 'loading' as const } }))
      .exhaustive()
})
